import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  CSB_REPORT_LIBRARY,
  createBatch,
  processBatch,
  retryFailedInBatch,
  subscribeToAllBatches,
  subscribeToBatch,
} from '../../../services/batchImportService';
import { useAuth } from '../../../contexts/AuthContext';
import { formatTimestamp } from '../../../utils/firestore';

/** Status chip color/label mapping */
const STATUS_CONFIG = {
  queued: { color: 'default', icon: <ScheduleIcon fontSize="small" />, label: 'Queued' },
  processing: { color: 'info', icon: <PendingIcon fontSize="small" />, label: 'Processing' },
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' },
  error: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Error' },
};

/**
 * BatchStatusView — shows batch progress with expandable document details.
 */
function BatchStatusView({ batch }) {
  const [expanded, setExpanded] = useState(false);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (!expanded || !batch.batchId) return;
    const unsub = subscribeToBatch(batch.batchId, setDocs);
    return unsub;
  }, [expanded, batch.batchId]);

  const progress = batch.total > 0
    ? Math.round(((batch.completed + batch.errored) / batch.total) * 100)
    : 0;

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {batch.batchName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(batch.createdAt)} — {batch.total} report{batch.total !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {batch.completed > 0 && (
            <Chip
              size="small"
              icon={<CheckCircleIcon />}
              label={`${batch.completed} done`}
              color="success"
              variant="outlined"
            />
          )}
          {batch.processing > 0 && (
            <Chip
              size="small"
              icon={<PendingIcon />}
              label={`${batch.processing} processing`}
              color="info"
              variant="outlined"
            />
          )}
          {batch.queued > 0 && (
            <Chip
              size="small"
              icon={<ScheduleIcon />}
              label={`${batch.queued} queued`}
              variant="outlined"
            />
          )}
          {batch.errored > 0 && (
            <Chip
              size="small"
              icon={<ErrorIcon />}
              label={`${batch.errored} failed`}
              color="error"
              variant="outlined"
            />
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Progress bar */}
      {batch.total > 0 && (batch.processing > 0 || progress > 0) && (
        <Box sx={{ px: 2, pb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={batch.errored > 0 ? 'warning' : 'primary'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {progress}% complete ({batch.completed + batch.errored}/{batch.total})
          </Typography>
        </Box>
      )}

      {/* Expanded document list */}
      <Collapse in={expanded}>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source URL</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d) => {
                const statusCfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.queued;
                return (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Typography variant="body2">{d.title}</Typography>
                      {d.incidentDate && (
                        <Typography variant="caption" color="text.secondary">
                          Incident: {d.incidentDate}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={statusCfg.icon}
                        label={statusCfg.label}
                        color={statusCfg.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {d.sourceUrl && (
                        <Tooltip title={d.sourceUrl}>
                          <IconButton
                            size="small"
                            href={d.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {d.error && (
                        <Typography variant="caption" color="error">
                          {d.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading batch details...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
}

/**
 * CSBBulkImport — Batch import interface for CSB reports.
 *
 * Allows superadmins to:
 * 1. Select from known CSB reports or add custom URLs
 * 2. Queue a batch for processing
 * 3. Fire n8n webhooks with rate limiting
 * 4. Track batch progress in real-time
 */
export default function CSBBulkImport() {
  const { user } = useAuth();

  // Library selection state
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [customReports, setCustomReports] = useState([]);
  const [customTitle, setCustomTitle] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [batchName, setBatchName] = useState('');

  // Processing state
  const [creating, setCreating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progressInfo, setProgressInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Batches
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);

  // Subscribe to all batches
  useEffect(() => {
    const unsub = subscribeToAllBatches((data) => {
      setBatches(data);
      setBatchesLoading(false);
    });
    return unsub;
  }, []);

  // Toggle library report selection
  const toggleReport = useCallback((reportId) => {
    setSelectedReports((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  }, []);

  // Select/deselect all library reports
  const toggleSelectAll = useCallback(() => {
    if (selectedReports.size === CSB_REPORT_LIBRARY.length) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(CSB_REPORT_LIBRARY.map((r) => r.id)));
    }
  }, [selectedReports.size]);

  // Add custom report
  const addCustomReport = useCallback(() => {
    if (!customTitle.trim() || !customUrl.trim()) return;
    setCustomReports((prev) => [
      ...prev,
      {
        id: `custom_${Date.now()}`,
        title: customTitle.trim(),
        url: customUrl.trim(),
        pdfUrl: customUrl.trim(),
        description: 'Custom CSB report',
        industry: 'oil_gas',
      },
    ]);
    setCustomTitle('');
    setCustomUrl('');
  }, [customTitle, customUrl]);

  // Remove custom report
  const removeCustomReport = useCallback((id) => {
    setCustomReports((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Create and process batch
  const handleCreateBatch = useCallback(async () => {
    setError(null);
    setSuccess(null);

    // Gather selected reports
    const libraryReports = CSB_REPORT_LIBRARY.filter((r) => selectedReports.has(r.id));
    const allReports = [...libraryReports, ...customReports];

    if (allReports.length === 0) {
      setError('Please select at least one report to import.');
      return;
    }

    setCreating(true);

    try {
      // Create the batch in Firestore
      const { batchId } = await createBatch({
        reports: allReports,
        createdBy: user?.uid || 'unknown',
        batchName: batchName.trim() || `CSB Import ${new Date().toLocaleDateString()}`,
      });

      setSuccess(`Batch created with ${allReports.length} report(s). Starting processing...`);
      setSelectedReports(new Set());
      setCustomReports([]);
      setBatchName('');
      setCreating(false);

      // Start processing
      setProcessing(true);
      setProgressInfo({ processed: 0, total: allReports.length, currentTitle: '' });

      const result = await processBatch(batchId, (info) => {
        setProgressInfo(info);
      });

      setProcessing(false);
      setProgressInfo(null);
      setSuccess(
        `Batch processing complete: ${result.succeeded} succeeded, ${result.failed} failed out of ${result.processed} total.`
      );
    } catch (err) {
      setCreating(false);
      setProcessing(false);
      setProgressInfo(null);
      setError(err.message || 'Failed to create batch');
    }
  }, [selectedReports, customReports, batchName, user]);

  // Retry failed documents in a batch
  const handleRetryBatch = useCallback(async (batchId) => {
    try {
      const count = await retryFailedInBatch(batchId);
      if (count === 0) {
        setSuccess('No failed documents to retry.');
        return;
      }

      setProcessing(true);
      setProgressInfo({ processed: 0, total: count, currentTitle: '' });

      const result = await processBatch(batchId, (info) => {
        setProgressInfo(info);
      });

      setProcessing(false);
      setProgressInfo(null);
      setSuccess(`Retry complete: ${result.succeeded} succeeded, ${result.failed} failed.`);
    } catch (err) {
      setProcessing(false);
      setProgressInfo(null);
      setError(err.message || 'Retry failed');
    }
  }, []);

  const totalSelected = selectedReports.size + customReports.length;

  return (
    <Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Processing progress */}
      {processing && progressInfo && (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
          Processing report {progressInfo.processed + 1} of {progressInfo.total}
          {progressInfo.currentTitle && `: ${progressInfo.currentTitle}`}
          <LinearProgress
            variant="determinate"
            value={Math.round((progressInfo.processed / progressInfo.total) * 100)}
            sx={{ mt: 1 }}
          />
        </Alert>
      )}

      {/* CSB Library Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">CSB Report Library</Typography>
          <Button size="small" onClick={toggleSelectAll}>
            {selectedReports.size === CSB_REPORT_LIBRARY.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select CSB investigation reports to import. Each report will be sent to the n8n processing
          pipeline for AI analysis and case study generation.
        </Typography>

        {CSB_REPORT_LIBRARY.map((report) => (
          <Paper
            key={report.id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1,
              bgcolor: selectedReports.has(report.id) ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => toggleReport(report.id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedReports.has(report.id)}
                    onChange={() => toggleReport(report.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label=""
                sx={{ mr: 0, mt: -0.5 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {report.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {report.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={report.industry} size="small" variant="outlined" />
                  {report.incidentDate && (
                    <Chip label={`Incident: ${report.incidentDate}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </Paper>

      {/* Custom URL Input */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Custom CSB Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add additional CSB report URLs not in the library above.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Report Title"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            disabled={processing}
          />
          <TextField
            label="Report URL or PDF URL"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            size="small"
            sx={{ flex: 2 }}
            disabled={processing}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addCustomReport}
            disabled={!customTitle.trim() || !customUrl.trim() || processing}
          >
            Add
          </Button>
        </Box>

        {customReports.length > 0 && (
          <Box>
            {customReports.map((report) => (
              <Box
                key={report.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'action.selected',
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {report.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {report.url}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => removeCustomReport(report.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Batch Name & Submit */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Create Import Batch
        </Typography>

        <TextField
          label="Batch Name (optional)"
          value={batchName}
          onChange={(e) => setBatchName(e.target.value)}
          placeholder={`CSB Import ${new Date().toLocaleDateString()}`}
          size="small"
          fullWidth
          disabled={processing || creating}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleCreateBatch}
            disabled={totalSelected === 0 || processing || creating}
            size="large"
          >
            {creating
              ? 'Creating Batch...'
              : `Import ${totalSelected} Report${totalSelected !== 1 ? 's' : ''}`}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {totalSelected} report{totalSelected !== 1 ? 's' : ''} selected
            ({selectedReports.size} from library, {customReports.length} custom)
          </Typography>
        </Box>
      </Paper>

      {/* Batch History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Import Batch History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Track progress of previous batch imports. Click a batch to see individual report status.
        </Typography>

        {batchesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : batches.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No import batches yet. Create your first batch above.
          </Typography>
        ) : (
          batches.map((batch) => (
            <Box key={batch.batchId}>
              <BatchStatusView batch={batch} />
              {batch.errored > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 1 }}>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => handleRetryBatch(batch.batchId)}
                    disabled={processing}
                    color="warning"
                  >
                    Retry Failed ({batch.errored})
                  </Button>
                </Box>
              )}
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
}
